"use client";

import "@/app/(public)/shared/tokens/colors.css";
import "@/app/(public)/shared/tokens/typography.css";
import type { JobListing } from "@/app/(public)/(company)/careers/_logic/constants";
import { motion } from "motion/react";
import Image from "next/image";
import type { ReactNode } from "react";
import { ApplyCard } from "./ApplyCard";

interface RolePageProps {
  /** The role: title, tagline, illustration, and where to apply. */
  listing: JobListing;
  /** The role's own description sections. */
  children: ReactNode;
}

/**
 * Shared shell for a `/careers/<slug>` page: the header, the description on the
 * left, and the apply card on the right. Every role renders through this, so the
 * three pages cannot drift apart on layout.
 *
 * @param listing - The role being shown.
 * @param children - The role's description sections.
 * @returns The role page.
 */
export const RolePage = ({ listing, children }: RolePageProps) => {
  // Render
  return (
    <motion.div
      className="container-page py-32 flex flex-col gap-16"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 relative">
              <Image src={listing.image} alt={listing.title} fill sizes="48px" className="object-contain" />
            </div>
            <h2 className="text-h2 text-landing-primary font-bold">{listing.title}</h2>
          </div>
          <p className="text-h4 text-landing-secondary">{listing.description}</p>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-12">
        {/* Job Description */}
        <div className="flex-1 lg:pr-6">{children}</div>

        {/* Apply */}
        <div className="w-full lg:w-[400px]">
          <ApplyCard applyUrl={listing.applyUrl} />
        </div>
      </div>
    </motion.div>
  );
};
